import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent } from './ui/Dialog';
import styled from 'styled-components';

// Product images from TrendyKool
const heroImage = 'https://trendykool.com/wp-content/uploads/2024/07/kf-Sd3e28b06fe374905a4342f8e7eafee64c-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp';
const detail1 = 'https://trendykool.com/wp-content/uploads/2024/07/kf-S1e6e8d41a3fd4916ae6c807c942cb290a-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp';
const detail2 = 'https://trendykool.com/wp-content/uploads/2024/07/kf-S7d231c0df2704791a767268c566fe465y-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep-760x760.webp';
const detail3 = 'https://trendykool.com/wp-content/uploads/2024/07/kf-S77a38d6f1fd8487ca5c7cd203b4dabd5K-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep-150x150.webp';
const detail4 = 'https://trendykool.com/wp-content/uploads/2024/07/kf-S5310a96b8fdb439c9faa7256745c95f27-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background-color: ${props => props.theme.colors.background.main};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
`;

const ContentWrapper = styled.div`
  max-width: 75rem;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Subtitle = styled.span`
  color: ${props => props.theme.colors.accent.gold};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MainImage = styled.div`
  position: relative;
  aspect-ratio: 3/4;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  overflow: hidden;
  cursor: pointer;
  transition: ${props => props.theme.transitions.slow};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-row: span 2;
  }
  
  &:hover {
    transform: scale(1.05);
    
    img {
      transform: scale(1.1);
    }
    
    .overlay {
      background-color: rgba(0, 0, 0, 0.2);
    }
    
    .zoom-icon {
      opacity: 1;
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.default};
`;

const ZoomIcon = styled(ZoomIn)`
  width: 2.5rem;
  height: 2.5rem;
  color: white;
  opacity: 0;
  transition: ${props => props.theme.transitions.default};
`;

const Thumbnail = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  overflow: hidden;
  cursor: pointer;
  transition: ${props => props.theme.transitions.slow};
  
  &:hover {
    transform: scale(1.05);
    
    img {
      transform: scale(1.1);
    }
    
    .overlay {
      background-color: rgba(0, 0, 0, 0.2);
    }
    
    .zoom-icon {
      opacity: 1;
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }
`;

const LightboxContent = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.95);
  border: none;
  padding: 0;
  max-width: 80rem;
  width: 100%;
`;

const LightboxImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: ${props => props.theme.spacing['2xl']};
`;

const LightboxImage = styled.img`
  max-height: 80vh;
  max-width: 100%;
  object-fit: contain;
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const LightboxNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  padding: ${props => props.theme.spacing.md};
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    width: 2rem;
    height: 2rem;
  }
  
  &.prev {
    left: ${props => props.theme.spacing.lg};
  }
  
  &.next {
    right: ${props => props.theme.spacing.lg};
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: ${props => props.theme.spacing.lg};
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const ThumbnailStrip = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding-bottom: ${props => props.theme.spacing['2xl']};
`;

const ThumbnailButton = styled.button`
  width: 4rem;
  height: 4rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  border: 2px solid ${props => props.isActive ? 'white' : 'transparent'};
  opacity: ${props => props.isActive ? 1 : 0.5};
  transition: ${props => props.theme.transitions.default};
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageGallery = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const images = [
    { src: heroImage, alt: t('gallery.main') },
    { src: detail1, alt: t('gallery.detail1') },
    { src: detail2, alt: t('gallery.detail2') },
    { src: detail3, alt: t('gallery.detail3') },
    { src: detail4, alt: t('gallery.detail4') },
  ];

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <Section>
      <Container>
        <ContentWrapper>
          <SectionHeader>
            <Subtitle>{t('gallery.subtitle')}</Subtitle>
            <Title>{t('gallery.title')}</Title>
          </SectionHeader>

          <GalleryGrid>
            <MainImage onClick={() => { setSelectedImage(0); setIsLightboxOpen(true); }}>
              <img src={images[0].src} alt={images[0].alt} />
              <Overlay className="overlay">
                <ZoomIcon className="zoom-icon" />
              </Overlay>
            </MainImage>

            {images.slice(1).map((image, index) => (
              <Thumbnail
                key={index}
                onClick={() => { setSelectedImage(index + 1); setIsLightboxOpen(true); }}
              >
                <img src={image.src} alt={image.alt} />
                <Overlay className="overlay">
                  <ZoomIcon className="zoom-icon" />
                </Overlay>
              </Thumbnail>
            ))}
          </GalleryGrid>

          <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
            <DialogContent as={LightboxContent}>
              <LightboxNavButton className="prev" onClick={prevImage}>
                <ChevronLeft />
              </LightboxNavButton>
              <LightboxNavButton className="next" onClick={nextImage}>
                <ChevronRight />
              </LightboxNavButton>

              <LightboxImageContainer>
                <LightboxImage
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                />
              </LightboxImageContainer>

              <ImageCounter>
                {selectedImage + 1} / {images.length}
              </ImageCounter>

              <ThumbnailStrip>
                {images.map((image, index) => (
                  <ThumbnailButton
                    key={index}
                    isActive={selectedImage === index}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={image.src} alt="" />
                  </ThumbnailButton>
                ))}
              </ThumbnailStrip>
            </DialogContent>
          </Dialog>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default ImageGallery;
